/**
 * Pre-game setup overlay: controller (human / CPU level) + character
 * selection for both players + map preset.
 *
 * Display names come from the core data table (src/core/characters.js);
 * the ability *descriptions* below are UI copy only — no rules logic here.
 * On start, calls onStart({ players: [kind, kind], characters: [id, id],
 * gimmicks, meter }) which the caller feeds into createGame() / createAI()
 * (meter = advantage-meter visibility, persisted in localStorage).
 *
 * hooks.onSelect (optional) is called on every selection click — main.js
 * wires it to the 'select' SE. hooks.onBack (optional) adds a
 * タイトルへ戻る button below the start button and is called on click.
 */

import { CHARACTERS } from '../core/characters.js';
import { PRESETS } from './presets.js';
import { renderPawnThumbnails } from './pieces.js';
import { PLAYER_KINDS, PLAYER_KIND_LABELS } from './cpu.js';

const CHARACTER_DESCRIPTIONS = {
  none: '標準ルール。特殊能力なし。',
  ninja: 'アクティブ1回: 壁1枚を飛び越えて直隣のマスへ（ジャンプで1手）。',
  mage: 'アクティブ1回: 設置済みの壁を1枚除去。除去した壁は消滅し、誰の手札にも戻らない。',
  heavyweight: 'パッシブ: 開始時の壁が2枚多い（10→12枚）。',
  explorer: 'パッシブ: 氷・沼を無効化。アクティブ1回: 斜めに1マス移動。',
  sprinter: 'アクティブ1回: 同じ方向へ2マス直進ダッシュ（間も着地も空きマス）。',
  phantom: 'アクティブ1回: 相手の駒と位置を入れ替える（双方ゴール経路が残ること。自分がゴール行に着地する入れ替えは不可）。',
  brute: 'アクティブ2回: 直隣の相手を真っ直ぐ奥へ最大2マス押し出す（塞がっていれば1マス）。',
  hexer: 'アクティブ1回: 相手を呪縛（次の相手ターンを丸ごと封じる＝移動・壁・能力すべて不可）。',
  thief: 'アクティブ1回: 直隣の相手から壁を1枚奪う（自分の手札+1）。',
};

// 形勢メーター表示のオン/オフ（既定オン）。localStorage に永続化。
const METER_PREF_KEY = 'palisade-meter';

function loadMeterPref() {
  try {
    return localStorage.getItem(METER_PREF_KEY) !== '0';
  } catch {
    return true; // localStorage が使えない環境では既定値
  }
}

function saveMeterPref(on) {
  try {
    localStorage.setItem(METER_PREF_KEY, on ? '1' : '0');
  } catch {
    // 永続化できなくても選択自体は有効
  }
}

export function createSetupScreen(root, onStart, hooks = {}) {
  const onSelect = () => hooks.onSelect?.();

  // 実際の駒メッシュを一度だけオフスクリーン描画したサムネイル
  // （WebGL が使えない環境では null → アイコンなしで表示）
  const thumbs = renderPawnThumbnails(96);

  const selection = {
    players: ['human', 'human'],
    characters: ['none', 'none'],
    presetId: PRESETS[0].id,
    meter: loadMeterPref(),
  };

  root.innerHTML = '';
  const panel = document.createElement('div');
  panel.id = 'setup-panel';

  const title = document.createElement('h1');
  title.textContent = 'Palisade — 対局セットアップ';
  panel.appendChild(title);

  // --- character pickers (one column per player) ---
  const charsWrap = document.createElement('div');
  charsWrap.className = 'setup-chars';
  const charButtons = [[], []];

  for (let p = 0; p < 2; p++) {
    const column = document.createElement('div');
    column.className = 'setup-column';
    // --- controller picker (human / CPU levels) ---
    const ctrlHead = document.createElement('h2');
    ctrlHead.innerHTML = `<span class="player-${p + 1}">${p === 0 ? '先手' : '後手'}</span> の操作`;
    column.appendChild(ctrlHead);

    const ctrlRow = document.createElement('div');
    ctrlRow.className = 'setup-controllers';
    const ctrlButtons = [];
    for (const kind of PLAYER_KINDS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'controller-btn';
      btn.textContent = PLAYER_KIND_LABELS[kind] ?? kind;
      btn.addEventListener('click', () => {
        selection.players[p] = kind;
        for (const b of ctrlButtons) b.classList.remove('selected');
        btn.classList.add('selected');
        onSelect();
      });
      if (kind === selection.players[p]) btn.classList.add('selected');
      ctrlButtons.push(btn);
      ctrlRow.appendChild(btn);
    }
    column.appendChild(ctrlRow);

    const head = document.createElement('h2');
    head.innerHTML = `<span class="player-${p + 1}">${p === 0 ? '先手' : '後手'}</span> のキャラクター`;
    column.appendChild(head);

    for (const def of Object.values(CHARACTERS)) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'setup-card';
      const iconUrl = thumbs && thumbs[def.id] ? thumbs[def.id][p] : null;
      card.innerHTML =
        (iconUrl ? `<img class="card-icon" src="${iconUrl}" alt="" />` : '') +
        `<span class="card-text">` +
        `<span class="card-name">${def.name}</span>` +
        `<span class="card-desc">${CHARACTER_DESCRIPTIONS[def.id] ?? ''}</span>` +
        `</span>`;
      card.addEventListener('click', () => {
        selection.characters[p] = def.id;
        for (const b of charButtons[p]) b.classList.remove('selected');
        card.classList.add('selected');
        onSelect();
      });
      if (def.id === selection.characters[p]) card.classList.add('selected');
      charButtons[p].push(card);
      column.appendChild(card);
    }
    charsWrap.appendChild(column);
  }
  panel.appendChild(charsWrap);

  // --- map presets ---
  const mapHead = document.createElement('h2');
  mapHead.textContent = 'マップ';
  panel.appendChild(mapHead);

  const mapsWrap = document.createElement('div');
  mapsWrap.className = 'setup-maps';
  const mapButtons = [];
  for (const preset of PRESETS) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'setup-card map-card';
    card.innerHTML =
      `<span class="card-text">` +
      `<span class="card-name">${preset.name}</span>` +
      `<span class="card-desc">${preset.description}</span>` +
      `</span>`;
    card.addEventListener('click', () => {
      selection.presetId = preset.id;
      for (const b of mapButtons) b.classList.remove('selected');
      card.classList.add('selected');
      onSelect();
    });
    if (preset.id === selection.presetId) card.classList.add('selected');
    mapButtons.push(card);
    mapsWrap.appendChild(card);
  }
  panel.appendChild(mapsWrap);

  // --- gimmick legend ---
  const legend = document.createElement('div');
  legend.className = 'setup-legend';
  legend.innerHTML =
    '<span><span class="swatch ice"></span>氷 = 進入方向へ滑る</span>' +
    '<span><span class="swatch swamp"></span>沼 = 止まると次ターン移動不可</span>' +
    '<span class="legend-note">探検家は両ギミック無効</span>';
  panel.appendChild(legend);

  // --- 形勢メーター表示トグル ---
  const meterRow = document.createElement('div');
  meterRow.className = 'setup-toggle-row';
  const meterLabel = document.createElement('span');
  meterLabel.className = 'toggle-label';
  meterLabel.innerHTML =
    '形勢メーター表示' +
    '<span class="toggle-note">対局中、画面上部に勝率の目安を表示します</span>';
  const meterBtn = document.createElement('button');
  meterBtn.type = 'button';
  meterBtn.className = 'toggle-btn';
  function renderMeterBtn() {
    meterBtn.textContent = selection.meter ? 'オン' : 'オフ';
    meterBtn.classList.toggle('on', selection.meter);
    meterBtn.setAttribute('aria-pressed', selection.meter ? 'true' : 'false');
  }
  meterBtn.addEventListener('click', () => {
    selection.meter = !selection.meter;
    saveMeterPref(selection.meter);
    renderMeterBtn();
    onSelect();
  });
  renderMeterBtn();
  meterRow.appendChild(meterLabel);
  meterRow.appendChild(meterBtn);
  panel.appendChild(meterRow);

  // --- start button ---
  const startBtn = document.createElement('button');
  startBtn.type = 'button';
  startBtn.id = 'start-btn';
  startBtn.textContent = '対局開始';
  startBtn.addEventListener('click', () => {
    const preset = PRESETS.find((p) => p.id === selection.presetId) ?? PRESETS[0];
    onStart({
      players: [...selection.players],
      characters: [...selection.characters],
      gimmicks: preset.gimmicks.map((g) => ({ ...g })),
      meter: selection.meter,
    });
  });
  panel.appendChild(startBtn);

  // --- back to title (ステージ選択画面と同じ導線) ---
  if (hooks.onBack) {
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.id = 'setup-back-btn';
    backBtn.textContent = 'タイトルへ戻る';
    backBtn.addEventListener('click', () => {
      onSelect();
      hooks.onBack();
    });
    panel.appendChild(backBtn);
  }

  root.appendChild(panel);

  return {
    show() {
      root.classList.remove('hidden');
    },
    hide() {
      root.classList.add('hidden');
    },
  };
}
